import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ModeStandbyOutlined, People, PersonOff } from "@mui/icons-material";

import { CardLayout, MTable } from "../v3";
import { VendorTableColumns } from "../../pages/vendor/constants";
import { PurchaseService } from "../../services";
import { EXPORT_VENDOR } from "../../constants";
import { derivePdfColumns, getPrintableRows, useHasPermission } from "../../utils";
import { handlePdfDownload, Listing } from "../pdf";
import { usePersistedTableState } from "../../hooks/usePersistedTableState";
import { formatExpenseAccountBalance } from "src/utils/formatters";

export const VendorList = ({ companyId = null, totalCount = 0, setTotalCount = () => {}, reloadData }) => {
  const [vendors, setVendors] = useState([]);
  // Remembered per visit: Back restores these, forward navigation starts from the defaults.
  const {
    filter: selectedFilter,
    setFilter: setSelectedFilter,
    searchText,
    setSearchText,
    sortModel,
    setSortModel,
    paginationModel: paginationState,
    setPaginationModel: setPaginationState,
  } = usePersistedTableState({
    tableId: "vendors",
    defaults: { filter: true, sortModel: [{ field: "updatedAt", sort: "desc" }] },
    filterValues: [true, false, null],
  });
  const [loading, setLoading] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);

  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);

  const hasExportVendors = useHasPermission(EXPORT_VENDOR);

  const fetchData = useCallback(
    async (vendorFilters = {}) => {
      setLoading(true);
      try {
        const { data: vendors } = await PurchaseService.getVendors({
          ...vendorFilters,
          type: "VENDOR",
          includeBalance: true,
          ...(companyId !== null && { companyId }),
        });
        const vendorsWithFormattedBalance = vendors?.rows?.map((vendor) => ({
          ...vendor,
          account: {
            ...vendor.account,
            formattedBalance: vendor.account?.balance ? formatExpenseAccountBalance(vendor.account.balance) : 0,
          },
        }));
        setVendors(vendorsWithFormattedBalance);
        setTotalCount(vendors?.count);
      } catch (error) {
        toast.error("Failed to fetch vendor list");
        console.log("🚀 ~ fetchData ~ error:", error);
      } finally {
        setLoading(false);
      }
    },
    [setTotalCount, companyId],
  );

  useEffect(() => {
    fetchData({
      sortBy: sortModel[0]?.field,
      sortOrder: sortModel[0]?.sort,
      page: paginationState.page + 1,
      limit: paginationState.pageSize,
      active: selectedFilter,
      name: searchText,
    });
  }, [fetchData, sortModel, paginationState, selectedFilter, searchText, reloadData]);

  const handleFilterChange = (value) => {
    const filterValue = value === "null" ? null : JSON.parse(value);
    setSelectedFilter(filterValue);
  };

  const handleSearchChange = (searchValue) => {
    setSearchText(searchValue);
  };

  const handlePaginationChange = (model) => {
    setPaginationState(model);
  };

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    try {
      const pdfTitle = pdfTitleMap[String(selectedFilter)] || "All";
      const pdfColumnHeaders = derivePdfColumns(VendorTableColumns);
      const printableRows = getPrintableRows(rows);

      const pdfDocument = (
        <Listing
          title={`Vendors : ${pdfTitle}`}
          columnHeaders={pdfColumnHeaders}
          tableRows={printableRows}
          company={user?.company}
        />
      );

      await handlePdfDownload({
        document: pdfDocument,
        pdfTitle: `Vendors : ${pdfTitle}`,
      });
    } catch (error) {
      toast.error("PDF generation failed");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const onViewClick = (params, event) => {
    navigate(`/vendor/${params.row.id}`);
  };

  const filterOptions = [
    { label: "All", value: "null", icon: <ModeStandbyOutlined /> },
    { label: "Active", value: true, icon: <People /> },
    {
      label: "Inactive",
      value: false,
      icon: <PersonOff />,
    },
  ];

  const pdfTitleMap = {
    null: "All",
    true: "Active",
    false: "Inactive",
  };

  const rows = vendors?.map((vendor, index) => {
    return {
      sl: index + 1 + paginationState.page * paginationState.pageSize,
      id: vendor.id,
      name: vendor.name,
      locality: vendor?.primaryAddress?.locality,
      primaryPhone: vendor.primaryPhone,
      balance: vendor?.account?.balance,
      formattedBalance: vendor?.account?.formattedBalance,
    };
  });

  return (
    <CardLayout>
      <MTable
        // Filter data
        selectedFilter={selectedFilter}
        filterOptions={filterOptions}
        onFilterChange={handleFilterChange}
        showFilterButton
        // Search data
        placeholder="Search vendor"
        searchText={searchText}
        onSearchChange={handleSearchChange}
        showSearchBar
        // Export data
        showExportButton={hasExportVendors}
        isPdfGenerating={isPdfGenerating}
        onExportPdfClick={handleDownloadPdfClick}
        // Table data
        columnHeaders={VendorTableColumns}
        tableRows={rows}
        rowCount={totalCount || 0}
        loading={loading}
        sortModel={sortModel}
        onSortChange={setSortModel}
        paginationModel={paginationState}
        onPaginationChange={handlePaginationChange}
        onRowClick={onViewClick}
      />
    </CardLayout>
  );
};
